import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import {
  Container,
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { botService } from '../services/api';

const EditBot: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [whitelistInput, setWhitelistInput] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    stopMessage: '',
    stopMessageConfirmation: '',
    resumeMessage: '',
    clientStopMessage: '',
    clientResumeMessage: '',
    clientStopConfirmation: '',
    clientResumeConfirmation: '',
    whitelist: [] as string[],
    whitelistMessage: '',
  });

  const { data: botData, isLoading } = useQuery(
    ['bot', id],
    async () => {
      const response = await botService.get(id!);
      return response.data;
    }
  );

  useEffect(() => {
    if (botData?.bot) {
      setFormData({
        name: botData.bot.name || '',
        stopMessage: botData.bot.stop_message || 'PARAR BOT',
        stopMessageConfirmation: botData.bot.stop_message_confirmation || '',
        resumeMessage: botData.bot.resume_message || 'CONTINUAR BOT',
        clientStopMessage: botData.bot.client_stop_message || 'PARAR',
        clientResumeMessage: botData.bot.client_resume_message || 'CONTINUAR',
        clientStopConfirmation: botData.bot.client_stop_confirmation || '🛑 Atendimento automático pausado. Para reativar, envie CONTINUAR.',
        clientResumeConfirmation: botData.bot.client_resume_confirmation || '✅ Atendimento automático reativado! Como posso ajudá-lo?',
        whitelist: botData.bot.whitelist || [],
        whitelistMessage: botData.bot.whitelist_message || 'Desculpe, você não tem permissão para interagir com este bot.',
      });
    }
  }, [botData]);

  const updateMutation = useMutation(
    (data: any) => botService.update(id!, data),
    {
      onSuccess: () => {
        navigate(`/bots/${id}`);
      },
      onError: (error: any) => {
        setError(error.response?.data?.error || 'Erro ao atualizar bot');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Nome do bot é obrigatório');
      return;
    }

    if (!formData.stopMessage.trim()) {
      setError('Mensagem de parada é obrigatória');
      return;
    }

    if (!formData.resumeMessage.trim()) {
      setError('Mensagem de reativação é obrigatória');
      return;
    }

    updateMutation.mutate({
      name: formData.name,
      stop_message: formData.stopMessage,
      stop_message_confirmation: formData.stopMessageConfirmation,
      resume_message: formData.resumeMessage,
      client_stop_message: formData.clientStopMessage,
      client_resume_message: formData.clientResumeMessage,
      client_stop_confirmation: formData.clientStopConfirmation,
      client_resume_confirmation: formData.clientResumeConfirmation,
      whitelist: formData.whitelist.length > 0 ? formData.whitelist : null,
      whitelist_message: formData.whitelistMessage,
    });
  };

  const handleAddWhitelistNumber = () => {
    const cleanNumber = whitelistInput.replace(/\D/g, '');
    if (cleanNumber && !formData.whitelist.includes(cleanNumber)) {
      setFormData({
        ...formData,
        whitelist: [...formData.whitelist, cleanNumber],
      });
      setWhitelistInput('');
    }
  };

  const handleRemoveWhitelistNumber = (numberToRemove: string) => {
    setFormData({
      ...formData,
      whitelist: formData.whitelist.filter(num => num !== numberToRemove),
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Editar Bot
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nome do Bot"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />

          {/* Controle do Dono do Chip */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom color="primary">
              👤 Controle do Dono do Chip
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configurações para VOCÊ (portador do chip) controlar o bot
            </Typography>
            
            <TextField
              fullWidth
              label="Mensagem de Parada"
              value={formData.stopMessage}
              onChange={(e) => setFormData({ ...formData, stopMessage: e.target.value })}
              margin="normal"
              required
              helperText="Comando que VOCÊ envia para pausar o bot"
            />

            <TextField
              fullWidth
              label="Confirmação de Parada (opcional)"
              value={formData.stopMessageConfirmation}
              onChange={(e) => setFormData({ ...formData, stopMessageConfirmation: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Mensagem enviada ao cliente quando VOCÊ pausa o bot"
            />
            
            <TextField
              fullWidth
              label="Mensagem de Reativação"
              value={formData.resumeMessage}
              onChange={(e) => setFormData({ ...formData, resumeMessage: e.target.value })}
              margin="normal"
              required
              helperText="Comando que VOCÊ envia para reativar o bot"
            />
          </Box>

          {/* Controle do Cliente */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom color="secondary">
              💬 Controle do Cliente
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configurações para o CLIENTE controlar o atendimento automático
            </Typography>
            
            <TextField
              fullWidth
              label="Comando de Parada do Cliente"
              value={formData.clientStopMessage}
              onChange={(e) => setFormData({ ...formData, clientStopMessage: e.target.value })}
              margin="normal"
              required
              helperText="Comando que o CLIENTE envia para pausar o bot"
            />
            
            <TextField
              fullWidth
              label="Confirmação de Parada para Cliente"
              value={formData.clientStopConfirmation}
              onChange={(e) => setFormData({ ...formData, clientStopConfirmation: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Mensagem enviada quando o CLIENTE pausa o bot"
            />
            
            <TextField
              fullWidth
              label="Comando de Reativação do Cliente"
              value={formData.clientResumeMessage}
              onChange={(e) => setFormData({ ...formData, clientResumeMessage: e.target.value })}
              margin="normal"
              required
              helperText="Comando que o CLIENTE envia para reativar o bot"
            />
            
            <TextField
              fullWidth
              label="Confirmação de Reativação para Cliente"
              value={formData.clientResumeConfirmation}
              onChange={(e) => setFormData({ ...formData, clientResumeConfirmation: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Mensagem enviada quando o CLIENTE reativa o bot"
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Whitelist de Contatos
            </Typography>
            <FormHelperText>
              Adicione números que podem interagir com o bot. Deixe vazio para permitir todos.
            </FormHelperText>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <TextField
                label="Número WhatsApp"
                value={whitelistInput}
                onChange={(e) => setWhitelistInput(e.target.value)}
                placeholder="5511999999999"
                size="small"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddWhitelistNumber();
                  }
                }}
              />
              <IconButton 
                color="primary" 
                onClick={handleAddWhitelistNumber}
                disabled={!whitelistInput.trim()}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.whitelist.map((number) => (
                <Chip
                  key={number}
                  label={number}
                  onDelete={() => handleRemoveWhitelistNumber(number)}
                  deleteIcon={<DeleteIcon />}
                />
              ))}
              {formData.whitelist.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nenhum número na whitelist (bot atende todos)
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="Mensagem para Contatos Não Autorizados"
              value={formData.whitelistMessage}
              onChange={(e) => setFormData({ ...formData, whitelistMessage: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Mensagem enviada para contatos que não estão na whitelist"
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={updateMutation.isLoading}
            >
              {updateMutation.isLoading ? <CircularProgress size={20} /> : 'Salvar'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate(`/bots/${id}`)}
              disabled={updateMutation.isLoading}
            >
              Cancelar
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditBot;